import { MentorshipCohortSessionLogRow } from './MentorshipService';

interface MentorshipSessionLogTableProps {
    sessions: MentorshipCohortSessionLogRow[];
    isLoading: boolean;
    emptyMessage: string;
}

const TYPE_COLUMN_TIP = '1:1 sessions only in this view.';
const WATCH_TIP = 'Session recordings are not available';
const VIEW_TIP = 'Not available yet';

const initials = (name: string | null): string => {
    const parts = (name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
};

const blank = (value: string | null | undefined): string => {
    const text = value == null ? '' : String(value).trim();
    return text === '' ? '—' : text;
};

const durationLabel = (minutes: number | null): string =>
    minutes == null ? '—' : `${minutes} min`;

const roleLabel = (role: string | null): string => {
    if (role === 'SUPER_ADMIN') return 'Super Admin';
    if (role === 'INSTITUTE_ADMIN') return 'Institute Admin';
    if (role === 'MENTOR') return 'Mentor';
    if (role === 'MENTEE') return 'Mentee';
    return blank(role);
};

const MentorshipSessionLogTable = ({ sessions, isLoading, emptyMessage }: MentorshipSessionLogTableProps) => {
    return (
        <div className="ms-sl-table-wrap">
            <table className="ms-sl-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Pair</th>
                        <th title={TYPE_COLUMN_TIP}>Type</th>
                        <th>Duration</th>
                        <th>Topic</th>
                        <th>Link</th>
                        <th>Logged by</th>
                        <th>Source</th>
                        <th>Rating</th>
                        <th>Recording</th>
                        <th>Feedback</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={11}>
                                <div className="ms-sl-empty">Loading sessions...</div>
                            </td>
                        </tr>
                    ) : sessions.length === 0 ? (
                        <tr>
                            <td colSpan={11}>
                                <div className="ms-sl-empty">{emptyMessage}</div>
                            </td>
                        </tr>
                    ) : (
                        sessions.map((row) => {
                            const mentor = blank(row.mentorName);
                            const mentee = blank(row.menteeName);
                            const link = row.sessionLink == null ? '' : String(row.sessionLink).trim();
                            const ratingLabel =
                                row.ratingStars == null ? '—' : `${row.ratingStars}★`;
                            return (
                                <tr key={row.id}>
                                    <td>{blank(row.occurredAt)}</td>
                                    <td>
                                        <div className="ms-sl-pair">
                                            <div className="ms-sl-avatars" aria-hidden="true">
                                                <span className="ms-sl-avatar ms-sl-avatar--mentor">
                                                    {initials(row.mentorName)}
                                                </span>
                                                <span className="ms-sl-avatar ms-sl-avatar--mentee">
                                                    {initials(row.menteeName)}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="ms-sl-pair-names">
                                                    {mentor} ↔ {mentee}
                                                </div>
                                                <div className="ms-sl-pair-campus">{blank(row.campusName)}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="ms-sl-chip ms-sl-chip--type" title={TYPE_COLUMN_TIP}>
                                            {row.type || '1:1'}
                                        </span>
                                    </td>
                                    <td>{durationLabel(row.durationMinutes)}</td>
                                    <td>{blank(row.title)}</td>
                                    <td>
                                        {link ? (
                                            <a
                                                className="ms-sl-link"
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Open link"
                                                title="Open link">
                                                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                                            </a>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td>
                                        <span className="ms-sl-chip ms-sl-chip--role">{roleLabel(row.loggedByRole)}</span>
                                    </td>
                                    <td>
                                        <span className="ms-sl-chip ms-sl-chip--source">{blank(row.source)}</span>
                                    </td>
                                    <td>{ratingLabel}</td>
                                    <td>
                                        <div className="ms-sl-actions">
                                            <button type="button" className="ms-sl-action" disabled title={WATCH_TIP}>
                                                <i className="bi bi-play-circle" aria-hidden="true" />
                                                Watch
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ms-sl-actions">
                                            <button type="button" className="ms-sl-action" disabled title={VIEW_TIP}>
                                                View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default MentorshipSessionLogTable;
